import useTranslator from "../../hooks/useTranslator";
import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  formatPropertyAddress,
  haveValue,
  shouldShowCapacityReport,
} from "../../helper/common";
import ConfirmRemovalModal from "./ConfirmRemovalModal";
import ButtonActionTooltip from "../ErrorModal/ButtonActionTooltip";
import AuthContext from "../../context/auth/AuthContext";
import { Avatar, Button, Checkbox, Drawer, Icon, TextField } from "../design";
import "./shareWorkspace.css";

const ShareWorkspaceModal = ({
  isOpen,
  onClose,
  wholeData: wholeDataMain,
  dataType,
  refreshFunc,
  isFromTenantProfile,
}) => {
  const { _t } = useTranslator();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const { workspaceId } = useParams();

  const [searchText, setSearchText] = useState("");
  const [selectedFields, setSelectedFields] = useState([]);
  const [selectAllSelected, setSelectAllSelected] = useState(false);
  const [confirmationPopup, setConfirmationPopup] = useState(false);
  const [confirmPopupType, setConfirmPopupType] = useState("");
  const [wholeData, setWholeData] = useState([]);
  const [currentLang, setCurrentLang] = useState(
    localStorage.getItem("i18nextLng").toLowerCase() || "en-us"
  );

  const handleOpenCapacityReport = () => {
    if (selectedFields?.length === 0) return;
    const params = new URLSearchParams();
    selectedFields.forEach((f) =>
      params.append("sharedprofileid", f.share_profile_id)
    );
    const base = workspaceId ? `/${workspaceId}` : "";
    window.open(
      `${base}/capacity-report?${params.toString()}`,
      "_blank",
      "noreferrer"
    );
  };

  useEffect(() => {
    setCurrentLang(localStorage.getItem("i18nextLng").toLowerCase() || "en-us");
  }, [localStorage.getItem("i18nextLng")]);

  const loopVals = useMemo(() => {
    if (!haveValue(searchText)) return wholeData;

    const searchLower = searchText.toLowerCase();
    // #NOTEL for application, we have participants, so we need to filter by participant's email or name
    return wholeData.filter((item) => {
      const participants = item?.application?.participants ?? [];
      // search by participant's email OR participant's name
      if (participants.length > 0) {
        return participants.some(
          (p) =>
            p?.email?.toLowerCase().includes(searchLower) ||
            p?.name?.toLowerCase().includes(searchLower) ||
            item?.label?.toLowerCase().includes(searchLower) ||
            item?.sub_val?.toLowerCase().includes(searchLower)
        );
      }
      return (
        item?.label?.toLowerCase().includes(searchLower) ||
        item?.sub_val?.toLowerCase().includes(searchLower)
      );
    });
  }, [wholeData, searchText]);

  useEffect(() => {
    if (dataType == "properties") {
      setWholeData(
        wholeDataMain?.map((val) => ({
          label: formatPropertyAddress(val, currentLang),
          value: val?._id,
        }))
      );
    } else {
      setWholeData(wholeDataMain);
    }
  }, [wholeDataMain]);

  const handleCheckboxChange = (selectedData) => (e) => {
    const isChecked = e.target.checked;

    setSelectedFields((prev) => {
      let updated;

      if (isChecked) {
        updated = [...prev, selectedData];
      } else {
        updated = prev.filter(
          (val) =>
            (val?.share_profile_id || val?.value) !==
            (selectedData?.share_profile_id || selectedData?.value)
        );
      }

      // 🔑 update selectAllSelected based on counts
      setSelectAllSelected(updated.length === wholeData.length);

      return updated;
    });
  };

  const handleSelectAll = () => {
    setSelectAllSelected((prev) => {
      if (prev) {
        setSelectedFields([]);
      } else {
        setSelectedFields(wholeData);
      }
      return !prev;
    });
  };

  const handleUnSelectAll = () => {
    setSelectedFields([]);
    setSelectAllSelected(false);
  };

  const handleCloseConfirmation = () => {
    setConfirmationPopup(false);
  };

  // all rows ticked (same rule as before: count equals the shown list)
  const allSelected = selectedFields?.length === loopVals?.length;
  const noneSelected = selectedFields?.length === 0;

  const isRowSelected = (vals) =>
    selectedFields.some(
      (item) =>
        (item?.share_profile_id || item?.value) ===
        (vals?.share_profile_id || vals?.value)
    );

  // left picture of a row: group icon for applications, avatar for tenants,
  // home icon for properties
  const renderRowVisual = (vals) => {
    if (vals?.application) {
      return (
        <span className="share-ws__tile">
          <Icon name="users" size={18} />
          {vals?.application?.participants?.length > 0 && (
            <span className="share-ws__count">
              {vals?.application?.participants?.length}
            </span>
          )}
        </span>
      );
    }
    if (isFromTenantProfile) return <Avatar name={vals?.label} size="md" />;
    return (
      <span className="share-ws__tile">
        <Icon name="home" size={18} />
      </span>
    );
  };

  return (
    <>
      <Drawer
        open={isOpen && !confirmationPopup}
        onClose={onClose}
        title={_t(isFromTenantProfile ? "manageTenants" : "manageProperties")}
        footer={
          <div className="share-ws__actions">
            <div
              className="share-ws__share"
              data-tooltip-id="button-action-tooltip"
            >
              <Button
                size="lg"
                block
                iconStart={<Icon name="send" />}
                disabled={noneSelected}
                onClick={(e) => {
                  setConfirmationPopup(true);
                  setConfirmPopupType("add");
                }}
              >
                {_t("shareWithAWorkspace")}
              </Button>
              {noneSelected && <ButtonActionTooltip />}
            </div>
            {dataType === "properties" && (
              <div data-tooltip-id="button-action-tooltip">
                {/* icon only, so Share keeps one line; the label is for
                    screen readers and the hover hint */}
                <Button
                  variant="danger"
                  size="lg"
                  className="share-ws__remove"
                  aria-label={_t("removeFromThisWorkspace")}
                  title={_t("removeFromThisWorkspace")}
                  disabled={noneSelected}
                  onClick={(e) => {
                    setConfirmationPopup(true);
                    setConfirmPopupType("remove");
                  }}
                >
                  <Icon name="trash" />
                </Button>
                {noneSelected && <ButtonActionTooltip />}
              </div>
            )}
            {dataType !== "properties" &&
              shouldShowCapacityReport(user?.current_workspace, user) && (
                <div data-tooltip-id="button-action-tooltip">
                  <Button
                    variant="secondary"
                    size="lg"
                    block
                    iconStart={<Icon name="eye" />}
                    disabled={noneSelected}
                    onClick={handleOpenCapacityReport}
                  >
                    {_t("capacityReport")}
                  </Button>
                  {noneSelected && <ButtonActionTooltip />}
                </div>
              )}
          </div>
        }
      >
        <TextField
          id="share-workspace-search"
          icon={<Icon name="search" />}
          placeholder={
            dataType == "properties"
              ? _t("searchProperties")
              : _t("searchTenants")
          }
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
          }}
          trailing={
            haveValue(searchText) && (
              <Button
                variant="ghost"
                size="sm"
                aria-label={_t("clearSearch")}
                onClick={() => {
                  setSearchText("");
                }}
              >
                <Icon name="close" size={16} />
              </Button>
            )
          }
        />

        {!haveValue(searchText) && (
          <div className="share-ws__select-all">
            <Checkbox
              checked={allSelected}
              indeterminate={!noneSelected && !allSelected}
              onChange={() => handleSelectAll()}
              label={
                allSelected
                  ? _t("allSelected")
                  : `${selectedFields?.length || 0} ${_t("of")} ${loopVals?.length || 0} ${_t("selected")}`
              }
            />
            {dataType !== "properties" && !noneSelected && (
              <div data-tooltip-id="button-action-tooltip">
                <Button
                  variant="ghost"
                  size="sm"
                  iconOnly
                  iconStart={<Icon name="trash" />}
                  aria-label={_t("removeFromThisWorkspace")}
                  onClick={(e) => {
                    if (selectedFields?.length === 0) return;
                    setConfirmationPopup(true);
                    setConfirmPopupType("remove");
                  }}
                />
              </div>
            )}
            <Button
              variant="link"
              size="sm"
              className="share-ws__unselect"
              onClick={handleUnSelectAll}
            >
              {_t("unselectAll")}
            </Button>
          </div>
        )}

        {loopVals?.length > 0 ? (
          <div className="share-ws__list">
            {loopVals?.map((vals) => (
              <Checkbox
                key={vals?.share_profile_id || vals?.value}
                className="share-ws__row"
                checked={isRowSelected(vals)}
                onChange={(checked) =>
                  handleCheckboxChange(vals)({ target: { checked } })
                }
                label={
                  <span className="share-ws__row-main">
                    {renderRowVisual(vals)}
                    <span className="share-ws__row-text">
                      <span className="share-ws__name">{vals?.label}</span>
                      {haveValue(vals?.sub_val) && (
                        <span className="share-ws__sub">{vals?.sub_val}</span>
                      )}
                    </span>
                  </span>
                }
              />
            ))}
          </div>
        ) : (
          <p className="share-ws__empty">{_t("noDataFound")}</p>
        )}
      </Drawer>
      {confirmationPopup && (
        <ConfirmRemovalModal
          confirmPopupType={confirmPopupType}
          selectedFields={selectedFields}
          onClose={handleCloseConfirmation}
          dataType={dataType}
          onCloseMain={onClose}
          refreshFunc={refreshFunc}
        />
      )}
    </>
  );
};

export default ShareWorkspaceModal;
